// Lógica pura del configurador «Módulos» (Configuración → Módulos).
// Sin JSX ni imports de valores: se prueba con `npm run test:unit` (node --test).

import type { ModuleDefinition } from '@/modules/types';
import type { ModuleSource, OrgModuleChange, OrgModuleItem } from '@/lib/api';

/** De dónde sale el estado del módulo, en palabras de la org. */
export const SOURCE_LABEL: Record<ModuleSource, string> = {
  org: 'Lo decidió tu organización',
  base: 'Incluido para toda organización',
  opt_in: 'Solo bajo pedido',
  arquetipo: 'Incluido por tu giro',
  todo: 'Incluido por tu giro',
};

export interface MenuSection {
  key: string;
  name: string;
  tabs: string[];
}

type RegistryEntry = Pick<ModuleDefinition, 'key' | 'name' | 'backendModuleKey' | 'sidebarItems' | 'byArchetype'>;

/** Secciones del menú que dependen de un módulo del backend (las que aparecen
 *  o desaparecen al prenderlo/apagarlo). Usa el nombre por giro si existe. */
export function menuSectionsFor(backendKey: string, registry: RegistryEntry[], archetype?: string | null): MenuSection[] {
  return registry
    .filter((m) => m.backendModuleKey === backendKey)
    .map((m) => {
      const over = archetype ? m.byArchetype?.[archetype] : undefined;
      const tabs = (m.sidebarItems ?? [])
        .filter((i) => !i.devOnly && !i.comingSoon)
        .map((i) => over?.sidebarItems?.[i.tab]?.label ?? i.label);
      return { key: m.key, name: over?.name ?? m.name, tabs };
    });
}

/** Minúsculas y sin acentos, para buscar «facturacion» y encontrar «Facturación». */
export function normalize(s: string): string {
  return (s || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

export type StateFilter = 'all' | 'on' | 'off';

export interface ModuleGroup {
  group: string;
  items: OrgModuleItem[];
}

/** Agrupa en el orden del backend; filtra por estado y búsqueda (nombre, descripción o clave). */
export function groupModules(
  modules: OrgModuleItem[],
  groups: string[],
  filter: StateFilter = 'all',
  query = '',
): ModuleGroup[] {
  const q = normalize(query);
  const visibles = modules.filter((m) => {
    if (filter === 'on' && !m.enabled) return false;
    if (filter === 'off' && m.enabled) return false;
    if (!q) return true;
    return [m.label, m.description, m.key, m.group].some((x) => normalize(x).includes(q));
  });
  const orden = [...groups, ...visibles.map((m) => m.group).filter((g) => !groups.includes(g))];
  const vistos = new Set<string>();
  const out: ModuleGroup[] = [];
  for (const g of orden) {
    if (vistos.has(g)) continue;
    vistos.add(g);
    const items = visibles.filter((m) => m.group === g).sort((a, b) => a.label.localeCompare(b.label, 'es'));
    if (items.length) out.push({ group: g, items });
  }
  return out;
}

/** «ana apagó CRM» — línea del historial. */
export function describeChange(c: OrgModuleChange, labelOf: (key: string) => string): string {
  const quien = c.actor || (c.via === 'god-mode' ? 'Soporte de la plataforma' : 'Alguien');
  const accion = c.enabled_after ? 'encendió' : 'apagó';
  return `${quien} ${accion} ${labelOf(c.key)}`;
}

/** «get_trip_resume» → «Get trip resume». Las tools llegan en snake_case. */
export function humanizeTool(name: string): string {
  const s = (name || '').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  return s ? s[0].toUpperCase() + s.slice(1) : '';
}
